import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { DashboardInjectedProps } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/util/types';
import {
  Tab,
  Tabs,
  TabList,
  TabPanel,
} from '@lifeomic/chroma-react/components/Tabs';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import SurveyList from './SurveyList';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import ResponseList from './ResponseList';
import { useActiveMrnConfig } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveMrnConfig';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import {
  Radio,
  RadioGroupMinimal,
} from '@lifeomic/chroma-react/components/Radio';
import { Box } from '@lifeomic/chroma-react/components/Box';
import useSurveysList, {
  UseSurveysListProvider,
} from '@lifeomic/phc-web-toolkit/src/components/ManageSurveys/SurveysList/useSurveysList';
import SurveySearchField from '@lifeomic/phc-web-toolkit/src/components/ManageSurveys/SurveysList/SurveySearchField';
import { useSurveysAndCount } from '@lifeomic/phc-web-toolkit/src/components/ManageSurveys/SurveysList/useSurveysAndCount';

const messages = defineMessages({
  surveyTab: {
    id: 'patientViewer.surveys.tabs.survey',
    defaultMessage: 'Surveys',
  },
  responsesTab: {
    id: 'patientViewer.surveys.tabs.responses',
    defaultMessage: 'Responses',
  },
  surveyOptions: {
    id: 'patientViewer.surveys.tabs.surveyOptions',
    defaultMessage: 'Survey options',
  },
  surveyFilter: {
    id: 'patientViewer.surveys.tabs.surveyFilter',
    defaultMessage: 'Filter surveys by status',
  },
  surveyFilterAll: {
    id: 'patientViewer.surveys.tabs.surveyFilterAll',
    defaultMessage: 'All',
  },
  surveyFilterComplete: {
    id: 'patientViewer.surveys.tabs.surveyFilterComplete',
    defaultMessage: 'Complete',
  },
  surveyFilterIncomplete: {
    id: 'patientViewer.surveys.tabs.surveyFilterIncomplete',
    defaultMessage: 'Incomplete',
  },
});

export const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'auto',
    minHeight: '100%',
  },
  tabContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingBottom: theme.spacing(1),
  },
  contentContainer: {
    width: '100%',
    minHeight: 0,
    flex: 1,
    overflowY: 'auto',
  },
  controlsContainer: {
    flex: 1,
  },
  tab: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.black[50],
    flex: 1,
  },
}));

export type SubjectSurveysOwnProps = {
  patient: fhir.Patient;
  defaultTab?: 'surveys' | 'responses';
  defaultStatusFilter?: 'all' | 'completed' | 'in-progress';
  dateTickFilter: DashboardInjectedProps['dateTickFilter'];
  applyDateFilter: boolean;
};

export type SurveySettings = Pick<SubjectSurveysOwnProps, 'applyDateFilter'>;

export const InternalSubjectSurveys: React.FC<SubjectSurveysOwnProps> = ({
  dateTickFilter,
  patient,
  applyDateFilter,
  defaultTab,
  defaultStatusFilter = 'completed',
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const { setStatusFilters } = useSurveysList();
  const {
    results: surveys,
    isLoading: isFetchingSurveys,
    hasMoreToLoad,
    loadMore,
  } = useSurveysAndCount();
  const [renderTrigger, setRenderTrigger] = React.useState(
    defaultTab === 'responses' ? true : false,
  );
  const [isResponseTab, setIsResponseTab] = React.useState(
    defaultTab === 'responses' ? true : false,
  );
  const [statusFilter, setStatusFilter] = React.useState(defaultStatusFilter);
  const mrnConfig = useActiveMrnConfig();

  React.useEffect(() => {
    setStatusFilters(['active']);
  }, [setStatusFilters]);

  return (
    <div className={classes.root}>
      <Tabs
        selectedTabId={defaultTab === 'responses' ? 'tab2' : 'tab1'}
        variant="pill"
      >
        <Box margin={1}>
          <TabList aria-label={formatMessage(messages.surveyOptions)}>
            <Tab stopId="tab1" onClick={() => setIsResponseTab(false)}>
              {formatMessage(messages.surveyTab)}
            </Tab>

            <Tab
              onClick={() => {
                setRenderTrigger(!renderTrigger);
                setIsResponseTab(true);
              }}
              stopId="tab2"
            >
              {formatMessage(messages.responsesTab)}
            </Tab>
          </TabList>

          <Box className={classes.controlsContainer}>
            <Divider direction="row" />

            {isResponseTab ? (
              <RadioGroupMinimal
                onChange={e => {
                  setStatusFilter(
                    e.target.value as 'all' | 'completed' | 'in-progress',
                  );
                }}
                title=""
                value={statusFilter}
                aria-label={formatMessage(messages.surveyFilter)}
              >
                <Radio
                  label={formatMessage(messages.surveyFilterAll)}
                  value="all"
                />
                <Radio
                  label={formatMessage(messages.surveyFilterComplete)}
                  value="completed"
                />
                <Radio
                  label={formatMessage(messages.surveyFilterIncomplete)}
                  value="in-progress"
                />
              </RadioGroupMinimal>
            ) : (
              <SurveySearchField />
            )}
          </Box>
        </Box>

        <TabPanel stopId="tab1" className={classes.tab}>
          <SurveyList
            patient={patient}
            mrnConfig={mrnConfig}
            isFetchingProjectSurveys={isFetchingSurveys}
            projectSurveys={surveys}
            hasMoreToLoad={hasMoreToLoad}
            loadMode={loadMore}
          />
        </TabPanel>

        <TabPanel stopId="tab2" className={classes.tab}>
          <ResponseList
            dateTickFilter={dateTickFilter}
            patient={patient}
            ignoreTimeFilter={applyDateFilter === false}
            renderTrigger={renderTrigger}
            statusFilter={statusFilter === 'all' ? undefined : statusFilter}
          />
        </TabPanel>
      </Tabs>
    </div>
  );
};

export type SubjectSurveysProps = SubjectSurveysOwnProps &
  DashboardInjectedProps;

const SubjectSurveys: React.FC<SubjectSurveysProps> = props => {
  return (
    <UseSurveysListProvider>
      <InternalSubjectSurveys {...props} />
    </UseSurveysListProvider>
  );
};

export default React.memo(SubjectSurveys);
